import * as Dialog from "@radix-ui/react-dialog";
import * as Switch from "@radix-ui/react-switch";
import { X } from "lucide-react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/cn";

/**
 * Primitivos de la consola.
 *
 * Chicos y sin variantes de mas: `imprenta` usa shadcn/ui, que es una copia de
 * componentes al repo. Copiar todo eso para cinco pantallas seria arrastrar
 * codigo que nadie va a mirar. Lo accesible —foco, escape, aria— lo pone Radix,
 * que ya esta en el stack declarado (PRD §8); lo visual es Tailwind.
 */

type Variante = "primario" | "secundario" | "peligro" | "fantasma";

const ESTILO_BOTON: Record<Variante, string> = {
  primario: "bg-brand-600 text-white hover:bg-brand-700 disabled:bg-neutral-300",
  secundario: "border border-neutral-300 text-neutral-800 hover:bg-neutral-200",
  peligro: "border border-red-300 text-red-700 hover:bg-red-50",
  fantasma: "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-200",
};

/**
 * `icono` es el `size="icon"` de `imprenta`: un cuadrado de 32 px con el ícono
 * centrado, para las acciones de una fila.
 *
 * En una tabla las acciones se repiten en cada renglón, así que el texto se lee
 * una vez y después es ancho gastado: allá las tres de Lista de Precios entran
 * en 96 px. El nombre no se pierde —va en `aria-label`, que es el que usa el
 * lector de pantalla, y en `title`, que es el tooltip del mouse—, así que
 * **una acción sin texto exige los dos**.
 */
type Tamano = "normal" | "icono";

const ESTILO_TAMANO: Record<Tamano, string> = {
  normal: "px-3 py-1.5",
  icono: "inline-flex h-8 w-8 items-center justify-center p-0",
};

export function Boton({
  variante = "primario",
  tamano = "normal",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante; tamano?: Tamano }) {
  return (
    <button
      type="button"
      {...props}
      className={`rounded-md text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${ESTILO_TAMANO[tamano]} ${ESTILO_BOTON[variante]} ${className}`}
    />
  );
}

export function Campo({
  etiqueta,
  ayuda,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { etiqueta: string; ayuda?: string }) {
  const id = props.id ?? props.name ?? etiqueta;
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm text-neutral-600">
        {etiqueta}
      </label>
      <input
        id={id}
        {...props}
        className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm text-neutral-900 disabled:text-neutral-500"
      />
      {ayuda ? <p className="text-xs text-neutral-500">{ayuda}</p> : null}
    </div>
  );
}

export function Etiqueta({
  children,
  tono = "neutral",
}: {
  children: ReactNode;
  tono?: "neutral" | "ok" | "aviso" | "error" | "info";
}) {
  const estilos = {
    neutral: "bg-neutral-200 text-neutral-700",
    ok: "bg-emerald-100 text-emerald-800",
    aviso: "bg-amber-100 text-amber-800",
    error: "bg-red-100 text-red-800",
    // Azul: «está pasando», distinto de «salió bien». `imprenta` lo usa para
    // EN_PRODUCCION, y sin este tono ese estado se pintaba de verde — el mismo
    // color que APROBADO, así que el chip no distinguía «lo aprobaron» de «lo
    // están haciendo».
    info: "bg-blue-100 text-blue-800",
  } as const;
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${estilos[tono]}`}>{children}</span>
  );
}

export function Interruptor({
  checked,
  onCheckedChange,
  disabled,
  "aria-label": ariaLabel,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  "aria-label": string;
}) {
  return (
    <Switch.Root
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className="h-5 w-9 shrink-0 rounded-full bg-neutral-700 transition-colors data-[state=checked]:bg-sky-600 disabled:opacity-40"
    >
      <Switch.Thumb className="block h-4 w-4 translate-x-0.5 rounded-full bg-white transition-transform data-[state=checked]:translate-x-[1.125rem]" />
    </Switch.Root>
  );
}

/**
 * Modal. Radix pone foco, escape y `aria-modal`; nosotros solo el aspecto.
 *
 * **Solo para formularios y confirmaciones** (ADR 0064). El detalle de una
 * entidad —una OT, una consulta, un producto— es una pantalla con su ruta, como
 * en `imprenta`: en un modal centrado no entra, y lo que no entra se lee peor
 * sin que nada falle.
 */
export function Modal({
  abierto,
  onCerrar,
  titulo,
  descripcion,
  ancho = "normal",
  children,
}: {
  abierto: boolean;
  onCerrar: () => void;
  titulo: string;
  descripcion?: string;
  /**
   * `ancho` y no un valor libre: son dos tamaños y no un parámetro de diseño
   * por pantalla. El grande existe para lo que lleva una TABLA adentro —los
   * precios de un producto son ocho columnas—, no para "que entre más texto".
   */
  ancho?: "normal" | "ancho";
  children: ReactNode;
}) {
  return (
    <Dialog.Root open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      <Dialog.Portal>
        {/* `z-50`: en móvil el contenido vive en un envoltorio `z-20` y el
            encabezado es `z-40`; sin z-index el portal se pinta debajo. */}
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content
          className={cn(
            // `max-h` + `flex-col` y el scroll SOLO en el cuerpo: sin esto el
            // modal crecía con su contenido y se salía de la pantalla por
            // arriba y por abajo —sin barra, porque el que scrollea es el
            // overlay `fixed`—, así que el final del formulario quedaba
            // inalcanzable. No fallaba nada: simplemente no se podía llegar.
            "-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50 flex max-h-[90vh] flex-col rounded-lg border border-neutral-200 bg-neutral-50 text-neutral-900",
            ancho === "ancho" ? "w-[min(56rem,94vw)]" : "w-[min(32rem,92vw)]",
          )}
        >
          {/* La cabecera y la X quedan fijas mientras el cuerpo scrollea: la X
              es de `imprenta`, donde todo diálogo la tiene, y un modal largo
              sin ella obliga a bajar hasta el final para encontrar «Cerrar». */}
          <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
            <div className="min-w-0">
              <Dialog.Title className="font-semibold text-lg">{titulo}</Dialog.Title>
              {descripcion ? (
                <Dialog.Description className="mt-1 text-neutral-600 text-sm">
                  {descripcion}
                </Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close
              aria-label="Cerrar"
              className="-mr-1 shrink-0 rounded-md p-1 text-neutral-400 transition-colors hover:bg-neutral-200 hover:text-neutral-700"
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function Encabezado({
  titulo,
  subtitulo,
  acciones,
}: {
  titulo: string;
  subtitulo?: string;
  acciones?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1 className="font-semibold text-xl">{titulo}</h1>
        {subtitulo ? <p className="mt-0.5 text-neutral-600 text-sm">{subtitulo}</p> : null}
      </div>
      {acciones ? <div className="flex gap-2">{acciones}</div> : null}
    </div>
  );
}

export function Aviso({
  children,
  tono = "error",
}: {
  children: ReactNode;
  tono?: "error" | "ok" | "neutral";
}) {
  const estilo = {
    error: "border-red-300 bg-red-50 text-red-800",
    ok: "border-emerald-300 bg-emerald-50 text-emerald-800",
    neutral: "border-neutral-300 bg-neutral-50 text-neutral-800",
  }[tono];
  // `alert` anuncia el error al lector de pantalla en cuanto aparece; los
  // demás son `status`, que no interrumpe.
  return (
    <div
      role={tono === "error" ? "alert" : "status"}
      className={`rounded-md border px-3 py-2 text-sm ${estilo}`}
    >
      {children}
    </div>
  );
}

/**
 * Una cabecera de tabla. El string alcanza para casi todas; el objeto existe
 * para las columnas de NÚMEROS, que van alineadas a la derecha igual que sus
 * celdas — una cabecera «Cantidad» a la izquierda sobre valores a la derecha se
 * lee como si perteneciera a otra columna. Es lo que hace `imprenta`.
 */
export type Cabecera = string | { texto: string; derecha: true };

export function Tabla({ cabeceras, children }: { cabeceras: Cabecera[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-neutral-200">
      {/* `min-w-max` ademas de `w-full`: sin eso la tabla se comprime dentro del
          contenedor y las celdas se parten en varias lineas en vez de scrollear,
          asi que el `overflow-x-auto` no llegaba a activarse nunca. Se vio con
          los precios de un producto, que son ocho columnas dentro de un modal. */}
      <table className="w-full min-w-max text-sm">
        <thead className="bg-white text-left text-neutral-600">
          <tr>
            {cabeceras.map((c) => {
              const texto = typeof c === "string" ? c : c.texto;
              return (
                <th
                  key={texto}
                  className={cn(
                    "px-4 py-2 font-medium",
                    typeof c === "string" ? undefined : "text-right",
                  )}
                >
                  {texto}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-200">{children}</tbody>
      </table>
    </div>
  );
}
